import {
  type AuthenticatedUser,
  type AuthTokens,
  type CompleteOnboardingInput,
  type OrgJoinPending,
  type DemoAccountList,
  type DemoLoginInput,
  type GoogleOauthAvailability,
  type TelegramMiniAppLoginInput,
  type LoginInput,
  type OnboardingTicketView,
  type RegisterInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const authApi = {
  /**
   * `skipAuthRefresh`: 401 ở đây là **sai mật khẩu**, không phải phiên hỏng. Để `apiFetch`
   * làm mới rồi gửi lại thì một lần gõ sai thành hai lần đếm vào `failed_login_attempts`.
   */
  login: (input: LoginInput) =>
    apiFetch<AuthTokens>('/auth/login', { method: 'POST', body: input, skipAuthRefresh: true }),

  /**
   * Đăng ký tạo **cả tổ chức lẫn người dùng đầu tiên**, và người đó là
   * `ORG_ADMIN` — chưa có tổ chức thì không có chỗ nào để mời người vào.
   */
  register: (input: RegisterInput) =>
    apiFetch<AuthTokens>('/auth/register', { method: 'POST', body: input }),

  /** Khách tạo tài khoản bằng link mời khách — APM-CLINV-c. Server mở phiên ngay. */

  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),

  /**
   * Phiếu onboarding — APM-GAUTH-a. Đọc để biết Google vừa xác minh email nào.
   *
   * Không nhận tham số: phiếu nằm trong cookie `HttpOnly` (APM-GAUTH-d), nên
   * trang không cầm nó và cũng không cần cầm. Một lượt ĐỌC — tải lại trang
   * không đốt phiếu.
   */
  onboardingTicket: () => apiFetch<OnboardingTicketView>('/auth/onboarding'),
  /** Phòng chờ: vào ngay khi email đã được mời — APM-GAUTH-e. */
  enterFromOnboarding: () => apiFetch<AuthTokens>('/auth/onboarding/enter', { method: 'POST' }),

  /**
   * Đổi phiếu lấy tài khoản thật; server mở phiên ngay trong lời gọi này. Ngoại lệ — thẻ 3
   * với link tổ chức cần duyệt (APM-PSPACE-m): `OrgJoinPending`, chưa tài khoản nên chưa có
   * phiên, phiếu còn nguyên cho phòng chờ.
   */
  completeOnboarding: (input: CompleteOnboardingInput) =>
    apiFetch<AuthTokens | OrgJoinPending>('/auth/onboarding', { method: 'POST', body: input }),

  me: () => apiFetch<AuthenticatedUser>('/auth/me'),

  /** Tài khoản trưng bày — APM-1318a. Rỗng khi chưa dựng tổ chức demo. */
  demoAccounts: () => apiFetch<DemoAccountList>('/auth/demo-accounts'),

  demoLogin: (input: DemoLoginInput) =>
    apiFetch<AuthTokens>('/auth/demo-login', { method: 'POST', body: input }),

  /** Có hiện nút Google không — APM-AUTH-g. `false` là trạng thái hợp lệ. */
  googleStatus: () => apiFetch<GoogleOauthAvailability>('/auth/google/status'),

  /** Đổi `initData` của Mini App lấy phiên — APM-TG-i. */
  telegramMiniApp: (input: TelegramMiniAppLoginInput) =>
    apiFetch<AuthTokens>('/auth/telegram/miniapp', { method: 'POST', body: input }),
};

export const authKeys = {
  demoAccounts: ['auth', 'demo-accounts'] as const,
  googleStatus: ['auth', 'google-status'] as const,
};
